import * as Sentry from "@sentry/react";

/**
 * Sentry Configuration for Production Monitoring
 * 
 * Captures:
 * - JavaScript errors and stack traces
 * - Performance metrics (TTI, CLS, FCP)
 * - User environment data
 * - API failures and latency
 */

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.MODE;
const IS_PRODUCTION = ENVIRONMENT === "production";

/**
 * Sanitize sensitive data before sending to Sentry
 */
const beforeSend = (event: Sentry.ErrorEvent) => {
  // Strip PII and sensitive fields
  if (event.request?.headers) {
    delete event.request.headers["authorization"];
    delete event.request.headers["cookie"];
  }

  // Remove sensitive data from breadcrumbs
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
      if (breadcrumb.data) {
        const sanitized = { ...breadcrumb.data };
        delete sanitized.password;
        delete sanitized.token;
        delete sanitized.apiKey;
        delete sanitized.email;
        return { ...breadcrumb, data: sanitized };
      }
      return breadcrumb;
    });
  }

  // Remove sensitive context
  if (event.user) {
    delete event.user.email;
    delete event.user.ip_address;
  }

  return event;
};

/**
 * Initialize Sentry monitoring
 * Only runs in production to avoid noise during development
 */
export const initSentry = () => {
  if (!IS_PRODUCTION || !SENTRY_DSN) {
    console.log("Sentry monitoring disabled:", IS_PRODUCTION ? "Missing DSN" : "Not in production");
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    
    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Session replay for error debugging
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance sampling
    tracesSampleRate: 0.2, // 20% of transactions
    
    // Session replay sampling
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Security & Privacy
    beforeSend,
    
    // Track releases for better debugging
    release: import.meta.env.VITE_APP_VERSION || "unknown",

    // Ignore common non-critical errors
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "Non-Error promise rejection captured",
      "Network request failed", // Handle these separately
    ],
  });

  console.log("✅ Sentry monitoring initialized");
};

/**
 * Capture custom error with context
 */
export const captureError = (
  error: Error,
  context?: Record<string, any>
) => {
  if (!IS_PRODUCTION) {
    console.error("Error (dev mode):", error, context);
    return;
  }

  Sentry.captureException(error, {
    extra: context,
  });
};

/**
 * Capture custom message/event
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = "info",
  context?: Record<string, any>
) => {
  if (!IS_PRODUCTION) {
    console.log(`[${level}]`, message, context);
    return;
  }

  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
};

/**
 * Set user context for error tracking
 */
export const setUserContext = (userId: string, email?: string) => {
  if (!IS_PRODUCTION) return;

  Sentry.setUser({
    id: userId,
    // Email is optional and should be sanitized
    username: email ? email.split("@")[0] : undefined,
  });
};

/**
 * Clear user context on logout
 */
export const clearUserContext = () => {
  if (!IS_PRODUCTION) return;
  Sentry.setUser(null);
};

/**
 * Add breadcrumb for debugging context
 */
export const addBreadcrumb = (
  message: string,
  category: string,
  data?: Record<string, any>
) => {
  if (!IS_PRODUCTION) return;

  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: "info",
  });
};

/**
 * Track performance metric
 */
export const trackPerformance = (
  name: string,
  value: number,
  unit: string = "millisecond"
) => {
  if (!IS_PRODUCTION) return;

  // Add as breadcrumb for context
  Sentry.addBreadcrumb({
    message: `Performance: ${name}`,
    category: "performance",
    data: { value, unit },
    level: "info",
  });
};

export { Sentry };
